import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';

/**
 * Ein Eintrag der Legende.
 *
 * `placeholder` ist der Text, der EINGEFÜGT wird — also inklusive der geschweiften Klammern.
 * Die Mailing-Editoren liefern ihn schon so; wer eine Liste ohne Klammern hat (z. B. den
 * LP-Katalog aus dem Backend), setzt sie beim Mappen.
 */
export interface PlaceholderLegendItem {
  placeholder: string;
  label: string;
  description: string;
  required?: boolean;
  /**
   * Aufgelöster Wert, falls bekannt. `null` heißt „auflösbar, aber hier ohne Wert" (z. B. ein
   * Webinar ohne Termin) — die Spalte sagt das dann ausdrücklich, statt leer zu bleiben.
   * `undefined` bei allen Einträgen blendet die Spalte ganz aus.
   */
  value?: string | null;
}

interface PlaceholderLegendProps {
  items: readonly PlaceholderLegendItem[];
  onInsert: (placeholder: string) => void;
  disabled?: boolean;
  /** Fußnote unter der Tabelle — inhaltlich je Editor verschieden, deshalb von außen. */
  hint?: React.ReactNode;
  /** Voreinstellung der Klappe. Aus in vollen Dialogen, an in schlanken. */
  defaultExpanded?: boolean;
}

/**
 * Ausklappbare Platzhalter-Legende mit Einfügen-Knopf.
 *
 * Lag bis 2026-09-04 in `scenes/webinar-mailings/` und war fest mit der Mail-Platzhalterliste
 * verdrahtet. Der LP-Editor braucht dieselbe Darstellung mit anderer Liste, deshalb kommen
 * Einträge und Fußnote jetzt von außen — statt einer dritten Kopie. Bewusst OHNE Default-Liste:
 * ein Import aus `scenes/` in `components/` wäre eine Rückwärts-Abhängigkeit.
 */
const PlaceholderLegend: React.FC<PlaceholderLegendProps> = ({
  items,
  onInsert,
  disabled = false,
  hint,
  defaultExpanded = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const zeigeWerte = items.some((i) => i.value !== undefined);

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          backgroundColor: '#f5f5f5',
          px: 2,
          py: 1,
          borderRadius: expanded ? '4px 4px 0 0' : '4px',
          '&:hover': { backgroundColor: '#eeeeee' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
          Verfügbare Platzhalter
        </Typography>
        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={expanded}>
        <TableContainer
          sx={{
            border: '1px solid #e0e0e0',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px',
            backgroundColor: '#ffffff',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell
                  sx={{ fontWeight: 'bold', color: '#333', width: zeigeWerte ? '28%' : '30%' }}
                >
                  Platzhalter
                </TableCell>
                {zeigeWerte && (
                  <TableCell sx={{ fontWeight: 'bold', color: '#333', width: '22%' }}>
                    Wird zu
                  </TableCell>
                )}
                <TableCell
                  sx={{ fontWeight: 'bold', color: '#333', width: zeigeWerte ? '35%' : '50%' }}
                >
                  Beschreibung
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', color: '#333', width: '15%', textAlign: 'center' }}
                >
                  Aktion
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={item.placeholder}
                  sx={{
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    '&:last-child td': { borderBottom: 'none' },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <code
                        style={{
                          backgroundColor: '#e3f2fd',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontFamily: 'monospace',
                          color: '#1565c0',
                        }}
                      >
                        {item.placeholder}
                      </code>
                      {item.required && (
                        <Chip
                          label="Pflicht"
                          size="small"
                          color="error"
                          sx={{ height: 20, fontSize: '10px' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  {zeigeWerte && (
                    <TableCell>
                      {item.value ? (
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                          {item.value}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: '#999', fontStyle: 'italic', fontSize: '0.85em' }}
                        >
                          kein Termin hinterlegt
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell sx={{ color: '#555' }}>
                    <Typography variant="body2">
                      <strong>{item.label}</strong> - {item.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onInsert(item.placeholder);
                      }}
                      disabled={disabled}
                      sx={{
                        textTransform: 'none',
                        fontSize: '12px',
                        py: 0.5,
                      }}
                    >
                      Einfügen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {hint && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666', pl: 1 }}>
            {hint}
          </Typography>
        )}
      </Collapse>
    </Box>
  );
};

export default PlaceholderLegend;
